import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type SmsSettingsDto, type SmsSettingsPayload } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const labelCls = "mb-1 block text-xs font-medium text-slate-600";
const cardCls = "space-y-3 rounded-xl border border-border bg-surface p-4 shadow-subtle";

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

interface Form {
  kannelEnabled: boolean;
  kannelUrl: string;
  kannelUser: string;
  kannelPassword: string;
  kannelFrom: string;
  kannelSmsc: string;
  yeastarEnabled: boolean;
  yeastarUrl: string;
  yeastarApiVersion: string;
  yeastarUser: string;
  yeastarPassword: string;
  yeastarTrunk: string;
  smsPrimary: "KANNEL" | "YEASTAR";
}

function toForm(d: SmsSettingsDto): Form {
  return {
    kannelEnabled: d.kannel.enabled,
    kannelUrl: d.kannel.url ?? "",
    kannelUser: d.kannel.user ?? "",
    kannelPassword: "",
    kannelFrom: d.kannel.from ?? "",
    kannelSmsc: d.kannel.smsc ?? "",
    yeastarEnabled: d.yeastar.enabled,
    yeastarUrl: d.yeastar.url ?? "",
    yeastarApiVersion: d.yeastar.apiVersion,
    yeastarUser: d.yeastar.user ?? "",
    yeastarPassword: "",
    yeastarTrunk: d.yeastar.trunk ?? "",
    smsPrimary: d.smsPrimary,
  };
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/** SMS podešavanja firme: Kannel i Yeastar S50 posebno (parametre daje HostArena). */
export function SmsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["sms-settings"], queryFn: () => api.getSmsSettings() });
  const [f, setF] = useState<Form | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState<Record<string, { ok: boolean; text: string }>>({});

  useEffect(() => {
    if (q.data && !f) setF(toForm(q.data));
  }, [q.data, f]);

  const save = useMutation({
    mutationFn: (p: SmsSettingsPayload) => api.saveSmsSettings(p),
    onSuccess: (d) => {
      setF((prev) => (prev ? { ...prev, kannelPassword: "", yeastarPassword: "" } : toForm(d)));
      qc.setQueryData(["sms-settings"], d);
      setMsg({ ok: true, text: "Sačuvano." });
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Čuvanje nije uspelo.") }),
  });
  const test = useMutation({
    mutationFn: (provider: "KANNEL" | "YEASTAR") => api.sendTestSms({ provider, to: testTo }),
    onSuccess: (r, provider) => {
      setTestMsg({ ...testMsg, [provider]: r.ok ? { ok: true, text: "Poruka je poslata." } : { ok: false, text: r.error ?? "Nije poslato." } });
      qc.invalidateQueries({ queryKey: ["sms-settings"] });
    },
    onError: (e, provider) => setTestMsg({ ...testMsg, [provider]: { ok: false, text: errMsg(e, "Nije poslato.") } }),
  });

  if (!f || !q.data) return <Skeleton className="h-60 w-full" />;
  const d = q.data;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF({ ...f, [k]: v });

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        Podatke za Kannel i Yeastar dobijaš od HostArene. Upiši ih ovde, sačuvaj i pošalji probnu poruku. Ako uključiš oba, drugi služi kao
        rezervni kad glavni ne radi.
      </p>
      {msg && <p className={`rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>{msg.text}</p>}

      <div className={cardCls}>
        <div className="grid gap-3 md:grid-cols-4">
          <Field label="Glavni način slanja">
            <select className={inputCls} value={f.smsPrimary} onChange={(e) => set("smsPrimary", e.target.value as Form["smsPrimary"])}>
              <option value="KANNEL">Kannel</option>
              <option value="YEASTAR">Yeastar S50</option>
            </select>
          </Field>
          <div className="flex items-end">
            <Button onClick={() => save.mutate({ smsPrimary: f.smsPrimary })} disabled={save.isPending}>
              Sačuvaj
            </Button>
          </div>
          <Field label="Broj za probnu poruku" hint="Proba ide sa sačuvanim podacima.">
            <input className={inputCls} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="064..." />
          </Field>
          <div className="text-sm text-slate-600">
            <div className="text-xs text-slate-500">Ovog meseca</div>
            poslato {d.thisMonth.sent}, neuspelo {d.thisMonth.failed}
          </div>
        </div>
      </div>

      <div className={cardCls}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Kannel</h2>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.kannelEnabled} onChange={(e) => set("kannelEnabled", e.target.checked)} /> uključen
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Adresa" hint="npr. http://sms.hostarena.rs:13013">
            <input className={inputCls} value={f.kannelUrl} onChange={(e) => set("kannelUrl", e.target.value)} />
          </Field>
          <Field label="Korisnik">
            <input className={inputCls} value={f.kannelUser} onChange={(e) => set("kannelUser", e.target.value)} />
          </Field>
          <Field label="Lozinka" hint={d.kannel.passwordSet ? "Upisana - prazno polje je ne menja." : "Nije upisana."}>
            <input type="password" className={inputCls} value={f.kannelPassword} onChange={(e) => set("kannelPassword", e.target.value)} />
          </Field>
          <Field label="Pošiljalac">
            <input className={inputCls} value={f.kannelFrom} onChange={(e) => set("kannelFrom", e.target.value)} />
          </Field>
          <Field label="SMSC (ako je dat)">
            <input className={inputCls} value={f.kannelSmsc} onChange={(e) => set("kannelSmsc", e.target.value)} />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() =>
              save.mutate({
                kannelEnabled: f.kannelEnabled,
                kannelUrl: f.kannelUrl || null,
                kannelUser: f.kannelUser || null,
                kannelFrom: f.kannelFrom || null,
                kannelSmsc: f.kannelSmsc || null,
                ...(f.kannelPassword ? { kannelPassword: f.kannelPassword } : {}),
              })
            }
            disabled={save.isPending}
          >
            Sačuvaj
          </Button>
          <Button variant="secondary" onClick={() => test.mutate("KANNEL")} disabled={!testTo || test.isPending}>
            Pošalji probni SMS
          </Button>
          {testMsg.KANNEL && <span className={`text-sm ${testMsg.KANNEL.ok ? "text-emerald-700" : "text-red-700"}`}>{testMsg.KANNEL.text}</span>}
        </div>
      </div>

      <div className={cardCls}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Yeastar S50 (SIM kartica)</h2>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.yeastarEnabled} onChange={(e) => set("yeastarEnabled", e.target.checked)} /> uključen
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Adresa centrale" hint="npr. https://pbx.hostarena.rs:8088">
            <input className={inputCls} value={f.yeastarUrl} onChange={(e) => set("yeastarUrl", e.target.value)} />
          </Field>
          <Field label="Verzija API-ja">
            <input className={inputCls} value={f.yeastarApiVersion} onChange={(e) => set("yeastarApiVersion", e.target.value)} />
          </Field>
          <Field label="GSM trunk (naziv)">
            <input className={inputCls} value={f.yeastarTrunk} onChange={(e) => set("yeastarTrunk", e.target.value)} />
          </Field>
          <Field label="API korisnik">
            <input className={inputCls} value={f.yeastarUser} onChange={(e) => set("yeastarUser", e.target.value)} />
          </Field>
          <Field label="API lozinka" hint={d.yeastar.passwordSet ? "Upisana - prazno polje je ne menja." : "Nije upisana."}>
            <input type="password" className={inputCls} value={f.yeastarPassword} onChange={(e) => set("yeastarPassword", e.target.value)} />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() =>
              save.mutate({
                yeastarEnabled: f.yeastarEnabled,
                yeastarUrl: f.yeastarUrl || null,
                yeastarApiVersion: f.yeastarApiVersion,
                yeastarUser: f.yeastarUser || null,
                yeastarTrunk: f.yeastarTrunk || null,
                ...(f.yeastarPassword ? { yeastarPassword: f.yeastarPassword } : {}),
              })
            }
            disabled={save.isPending}
          >
            Sačuvaj
          </Button>
          <Button variant="secondary" onClick={() => test.mutate("YEASTAR")} disabled={!testTo || test.isPending}>
            Pošalji probni SMS
          </Button>
          {testMsg.YEASTAR && <span className={`text-sm ${testMsg.YEASTAR.ok ? "text-emerald-700" : "text-red-700"}`}>{testMsg.YEASTAR.text}</span>}
        </div>
      </div>

      {d.recent.length > 0 && (
        <div className={cardCls}>
          <h2 className="font-semibold text-slate-900">Poslednje poruke</h2>
          {d.recent.map((m) => (
            <div key={m.id} className="border-t border-border py-1 text-xs first:border-t-0">
              {new Date(m.createdAt).toLocaleString("sr-Latn-RS", { dateStyle: "short", timeStyle: "short" })} · {m.to} · {m.provider} ·{" "}
              <span className={m.status === "SENT" ? "text-emerald-700" : "text-red-700"}>{m.status === "SENT" ? "poslato" : "neuspelo"}</span>
              {m.error ? ` · ${m.error}` : ""} · {m.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
